import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { formatPrice } from "@/utils/formatPrice";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Crown, Ticket, Users, Loader2, Edit2, CheckCircle2, XCircle } from "lucide-react";
import { format, addMonths, addYears } from "date-fns";
import { sv } from "date-fns/locale";

import type { Tables, TablesInsert } from "@/integrations/supabase/types";

type MembershipPlan = Tables<"membership_plans">;
type _MembershipWithPlan = Tables<"memberships"> & {
  membership_plans: { name: string } | null;
};

// intervalLabels moved inside component to use t()

export default function AdminMembership() {
  const { t } = useTranslation("admin");
  const intervalLabels: Record<string, string> = { month: t("membership.month"), quarter: t("membership.quarter"), year: t("membership.year") };
  const { organization } = useOrganization();
  const { categories: dbCategories } = useCustomerCategories(organization?.id);
  const qc = useQueryClient();
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MembershipPlan | null>(null);

  // Plan form state
  const [planName, setPlanName] = useState("");
  const [planDesc, setPlanDesc] = useState("");
  const [planPrice, setPlanPrice] = useState("");
  const [planInterval, setPlanInterval] = useState("month");
  const [planCategoryId, setPlanCategoryId] = useState("");

  // Queries
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["admin-plans", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("membership_plans")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: memberships = [], isLoading: _memLoading } = useQuery({
    queryKey: ["admin-memberships", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("memberships")
        // T1/G-030: interval fetched so approve can derive period_end per plan.
        .select("*, membership_plans(name, interval)")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: punchCards = [] } = useQuery({
    queryKey: ["admin-punch-cards", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("punch_cards")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("purchased_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // Mutations
  const savePlanMutation = useMutation({
    mutationFn: async () => {
      const payload: TablesInsert<"membership_plans"> = {
        organization_id: organization!.id,
        name: planName,
        description: planDesc || null,
        price: Math.round(parseFloat(planPrice)),
        interval: planInterval,
        category_id: planCategoryId || null,
      };
      if (editingPlan) {
        const { error } = await supabase.from("membership_plans").update(payload).eq("id", editingPlan.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("membership_plans").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success(editingPlan ? t("membership.planUpdated") : t("membership.planCreated"));
      setPlanDialog(false);
      resetPlanForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePlanMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("membership_plans").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plans"] }),
  });

  const membershipActionMutation = useMutation({
    mutationFn: async ({ id, status, interval }: { id: string; status: string; interval?: string | null }) => {
      const update: Record<string, unknown> = { status };
      if (status === "active") {
        // T1/G-030: period end follows the PLAN's billing interval
        // (month/quarter/year — trigger-validated set on membership_plans)
        // instead of the old hardcoded +30 days that shortchanged
        // quarterly/yearly memberships. Unknown/missing interval falls
        // back to +1 month (closest to the old behavior).
        const now = new Date();
        const periodEnd =
          interval === "year" ? addYears(now, 1)
          : interval === "quarter" ? addMonths(now, 3)
          : addMonths(now, 1);
        update.current_period_start = now.toISOString();
        update.current_period_end = periodEnd.toISOString();
      }
      const { error } = await supabase.from("memberships").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      qc.invalidateQueries({ queryKey: ["admin-memberships"] });
      toast.success(status === "active" ? t("membership.approved") : t("membership.rejected"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetPlanForm = () => {
    setPlanName(""); setPlanDesc(""); setPlanPrice(""); setPlanInterval("month");
    setPlanCategoryId("");
    setEditingPlan(null);
  };

  const openEditPlan = (plan: MembershipPlan) => {
    setEditingPlan(plan);
    setPlanName(plan.name);
    setPlanDesc(plan.description || "");
    setPlanPrice(plan.price.toString());
    setPlanInterval(plan.interval);
    setPlanCategoryId(plan.category_id || "");
    setPlanDialog(true);
  };

  if (plansLoading) return <Skeleton className="h-60" />;

  const pendingMembers = memberships.filter((m) => m.status === "pending");
  const activeMembers = memberships.filter((m) => m.status === "active");

  return (
    <div className="space-y-6">
      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans" className="gap-1"><Crown className="h-3.5 w-3.5" /> {t("membership.plans")}</TabsTrigger>
          <TabsTrigger value="members" className="gap-1">
            <Users className="h-3.5 w-3.5" /> {t("membership.members")}
            {pendingMembers.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                {pendingMembers.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="punch" className="gap-1"><Ticket className="h-3.5 w-3.5" /> {t("membership.punchCards")}</TabsTrigger>
          {/* T1/G-046: vouchers tab removed — AdminVouchers (Inställningar →
              Rabattkoder, AdminSettings case "vouchers") is the canonical
              voucher UI; this was a drifting duplicate. */}
        </TabsList>

        {/* PLANS */}
        <TabsContent value="plans" className="space-y-4 mt-3">
          <Button onClick={() => { resetPlanForm(); setPlanDialog(true); }} className="gap-1">
            <Plus className="h-4 w-4" /> {t("membership.newPlan")}
          </Button>
          {plans.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">{t("membership.noPlans")}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {plans.map((p) => (
                <Card key={p.id}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{p.name}</h4>
                      <Badge variant={p.is_active ? "default" : "secondary"}>
                        {p.is_active ? t("common.active") : t("common.inactive")}
                      </Badge>
                    </div>
                    <p className="text-lg font-bold">{formatPrice(p.price)}<span className="text-xs text-muted-foreground font-normal">/{intervalLabels[p.interval]}</span></p>
                    <div className="flex flex-wrap gap-1 text-xs">
                      {(() => {
                        const cat = dbCategories.find(c => c.id === p.category_id);
                        return cat ? <Badge variant="outline">{t("membership.category")}: {cat.name}</Badge> : <Badge variant="secondary">{t("membership.noCategory")}</Badge>;
                      })()}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => openEditPlan(p)} className="gap-1">
                        <Edit2 className="h-3 w-3" /> {t("common.edit")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => togglePlanMutation.mutate({ id: p.id, is_active: !p.is_active })}>
                        {p.is_active ? t("common.deactivate") : t("common.activate")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="space-y-4 mt-3">
          {/* Pending section */}
          {pendingMembers.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-1">{t("membership.pendingApproval")} ({pendingMembers.length})</h4>
              {pendingMembers.map((m) => (
                <Card key={m.id} className="border-amber-500/30">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{m.user_id?.slice(0, 8) || t("common.unknown")}</p>
                      <p className="text-xs text-muted-foreground">{m.membership_plans?.name}</p>
                    </div>
                    <Badge variant="secondary">Pending</Badge>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => membershipActionMutation.mutate({ id: m.id, status: "active", interval: m.membership_plans?.interval })}
                        title={t("membership.approve")}
                      >
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => membershipActionMutation.mutate({ id: m.id, status: "cancelled" })}
                        title={t("membership.reject")}
                      >
                        <XCircle className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Active members */}
          <h4 className="font-medium text-sm">{t("membership.activeMembers")} ({activeMembers.length})</h4>
          {activeMembers.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">{t("membership.noActiveMembers")}</p>
          ) : (
            activeMembers.map((m) => (
              <Card key={m.id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{m.user_id?.slice(0, 8) || t("common.unknown")}</p>
                    <p className="text-xs text-muted-foreground">{m.membership_plans?.name}</p>
                  </div>
                  <Badge variant="default">{t("common.active")}</Badge>
                  {m.current_period_end && (
                    <span className="text-xs text-muted-foreground">
                      {t("membership.until")} {format(new Date(m.current_period_end), "d MMM", { locale: sv })}
                    </span>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* PUNCH CARDS */}
        <TabsContent value="punch" className="space-y-3 mt-3">
          {punchCards.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">{t("membership.noPunchCards")}</p>
          ) : (
            punchCards.map((pc) => (
              <Card key={pc.id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{pc.user_id?.slice(0, 8) || t("common.unknown")}</p>
                    <p className="text-xs text-muted-foreground">{pc.name} · {pc.used_punches}/{pc.total_punches} {t("membership.punchesUsed")}</p>
                  </div>
                  <Badge variant={pc.status === "active" ? "default" : "secondary"}>{t(`membership.punchStatus.${pc.status}`, { defaultValue: pc.status })}</Badge>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

      </Tabs>

      {/* Plan dialog */}
      <Dialog open={planDialog} onOpenChange={setPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPlan ? t("membership.editPlan") : t("membership.newMembershipPlan")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder={t("membership.planNamePlaceholder")} /></div>
            <div className="space-y-2"><Label>{t("common.description")}</Label><Textarea value={planDesc} onChange={e => setPlanDesc(e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("common.priceKr")}</Label><NumberInput value={planPrice} onValueChange={v => setPlanPrice(String(v))} placeholder="299" /></div>
              <div className="space-y-2">
                <Label>{t("membership.interval")}</Label>
                <Select value={planInterval} onValueChange={setPlanInterval}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">{t("membership.month")}</SelectItem>
                    <SelectItem value="quarter">{t("membership.quarter")}</SelectItem>
                    <SelectItem value="year">{t("membership.year")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("membership.customerCategory")}</Label>
              <p className="text-xs text-muted-foreground">{t("membership.customerCategoryHint")}</p>
              <Select value={planCategoryId} onValueChange={setPlanCategoryId}>
                <SelectTrigger><SelectValue placeholder={t("membership.selectCategory")} /></SelectTrigger>
                <SelectContent>
                  {dbCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => savePlanMutation.mutate()} disabled={savePlanMutation.isPending || !planName || !planPrice}>
              {savePlanMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : null}
              {editingPlan ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
